import {
  Container,
  InputField,
  PasswordField,
  ResetPasswordLink,
  SubmitButton
} from '@/components/Auth/Buttons';
import { PasswordRequirements } from '@/components/Auth/PasswordRequirements';
import FreeTrialModal from '@/components/Modals/FreeTrialModal';
import { useAuth } from '@/providers/AuthProvider';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface SignInInput {
  username: string;
  password: string;
}

/**
 * SignIn component for user authentication
 */
export default function SignIn() {
  const [loading, setLoading] = useState(false);
  const [challengeSignIn, setChallengeSignIn] = useState(false);
  const [input, setInput] = useState<SignInInput>({ username: '', password: '' });
  const [challenge, setChallenge] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [showPassword, setShowPassword] = useState(false);
  const [showFreeTrialModal, setShowFreeTrialModal] = useState(false);
  const [signInReady, setSignInReady] = useState(false);

  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const username = searchParams.get('username');
    const password = searchParams.get('password');

    if (username || password) {
      setInput((prev) => ({
        ...prev,
        username: username ?? prev.username,
        password: password ?? prev.password
      }));
    }
  }, [searchParams]);

  // Show trial expired modal if user was logged out due to expired trial
  useEffect(() => {
    if (auth.loaded && !auth.user && auth.isTrialExpired) {
      setShowFreeTrialModal(true);
    }
  }, [auth.loaded, auth.user, auth.isTrialExpired, signInReady]);

  useEffect(() => {
    if (auth.loaded && auth.user && !auth.isTrialExpired && signInReady) {
      navigate('/');
      return;
    }

    if (signInReady && showFreeTrialModal) {
      setInput((prev) => ({
        ...prev,
        username: '',
        password: ''
      }));
      setSignInReady(false);
    }
  }, [signInReady, showFreeTrialModal, auth.loaded, auth.user, auth.isTrialExpired]);

  /**
   * Handles input changes for username and password
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setError(undefined);
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Handles the sign in process
   */
  const signIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(undefined);

    try {
      if (challengeSignIn) {
        await auth.confirmSignIn({ challengeResponse: challenge.trim() });
        navigate('/');
      } else {
        const normalizedUsername = input.username.trim().toLowerCase();
        const trimmedPassword = input.password.trim();
        const result = await auth.signIn({
          username: normalizedUsername,
          password: trimmedPassword
        });
        const step = result?.nextStep?.signInStep as string | undefined;

        if (step === 'RESET_PASSWORD') {
          navigate(
            `/auth/request-reset?username=${encodeURIComponent(normalizedUsername)}&migrated=true`
          );
          return;
        }

        if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setChallengeSignIn(true);
          return;
        }

        if (step === 'DONE') {
          setSignInReady(true);
          return;
        }
        setError('Sign in could not be completed. Please try again.');
      }
    } catch (err: any) {
      const name = err?.name || '';
      const message = err?.message || '';
      const normalizedUsername = input.username.trim().toLowerCase();

      if (name === 'UserAlreadyAuthenticatedException') {
        navigate('/');
        return;
      }

      if (
        name === 'PasswordResetRequiredException' ||
        message.includes('Password reset required')
      ) {
        navigate(
          `/auth/request-reset?username=${encodeURIComponent(normalizedUsername)}&migrated=true`
        );
        return;
      }

      if (message.includes('MIGRATED_USER_RESET_REQUIRED')) {
        navigate(
          `/auth/request-reset?username=${encodeURIComponent(normalizedUsername)}&migrated=true`
        );
        return;
      }

      setError(message || 'Sign in failed');
      console.error('Error during sign in:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggles password visibility
   */
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  return (
    <Container
      title="Welcome Back To Scribl"
      subtitle="Sign in and let the fun continue"
      banner={error}
      className="relative md:w-600 px-20 py-32 md:px-100 md:py-32 rounded-[12px] bg-white backdrop-blur-xs shadow-xs z-1"
    >
      <form className="w-full flex flex-col space-y-4" onSubmit={signIn}>
        {challengeSignIn ? (
          <>
            <p className="my-2">Before you can sign in, you will need to choose a new password.</p>
            <div className="group">
              <PasswordField
                label="New Password"
                value={challenge}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setChallenge(e.target.value)}
                showPassword={showPassword}
                togglePasswordVisibility={togglePasswordVisibility}
              />
              <div className="hidden group-focus-within:block">
                <PasswordRequirements password={challenge} />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mb-12">
              <InputField
                aria-label="Email"
                name="username"
                label="Email Address"
                value={input.username}
                onChange={handleInputChange}
              />
            </div>
            <div className="mb-12">
              <PasswordField
                value={input.password}
                onChange={handleInputChange}
                showPassword={showPassword}
                togglePasswordVisibility={togglePasswordVisibility}
              />
            </div>
          </>
        )}
        <SubmitButton
          title={challengeSignIn ? 'Confirm' : 'Sign In'}
          loading={loading}
          onSubmit={signIn}
        />
      </form>
      <div className="mt-16">
        <ResetPasswordLink />
      </div>

      <FreeTrialModal isOpen={showFreeTrialModal} onClose={() => setShowFreeTrialModal(false)} />
    </Container>
  );
}
